<template>
  <div class="reservation-container">
    <!-- Navigation -->
    <div class="main-navigation hidden-md-and-down">
      <front-navigation></front-navigation>
    </div>

    <div class="booking-wrapper">
      <!-- Sidebar -->
      <div class="sidebar-wrapper">
        <side-bar></side-bar>
      </div>

      <!-- Main Content -->
      <div class="main-content">
        <div class="search-wrapper">
          <search></search>
        </div>

        <!-- Stepper -->
        <div class="stepper-section">
          <div class="stepper-header">
            <div class="stepper-progress" :style="progressStyle"></div>
            <div class="stepper-steps">
              <div v-for="step in steps"
                   :key="step.id"
                   :class="['step', { 'active': model === step.id, 'completed': model > step.id }]"
                   @click="model = step.id">
                <div class="step-icon">
                  <transition name="icon-transition" mode="out-in">
                    <component :is="model >= step.id ? step.activeIcon : step.inactiveIcon"
                               :key="step.id + (model >= step.id ? 'active' : 'inactive')" />
                  </transition>
                </div>
                <div class="step-label">
                  <span class="step-number">Step {{step.id}}</span>
                  <span class="step-title earthwerk-font">{{step.title}}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Step Content -->
          <div class="stepper-content">
            <transition name="slide-fade" mode="out-in">
              <!-- Step 1 - Accommodation -->
              <div v-if="model === 1" key="step1" class="step-content">
                <facility-booking></facility-booking>

                <div class="action-buttons">
                  <button class="earth-action-btn" @click="nextStep">
                    <span>Skip Facilities </span>
                    <span>Done with Accommodation</span>
                    <svg class="arrow-icon" viewBox="0 0 24 24">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>

                <div class="divider"></div>

                <div class="facility-details">
                  <div class="details-grid">
                    <div class="details-info">
                      <h3 class="facility-name earthwerk-font">{{singleFacility.name}}</h3>

                      <div class="price-section">
                        <span v-if="singleFacility.accomodation_type_id === 'Pelican' && singleFacility.facility_type_id === 'Accomodation'">
                          <p class="price-label">Price Packs</p>
                          <div class="price-chips">
                            <div v-for="(price, key) in developPricePacks(singleFacility)" :key="key" class="price-chip">
                              <span>From <b>{{price.lower_limit}}</b> to <b>{{price.upper_limit}}</b> : <b>{{$localCurrency}} {{$roundOff(convert1(price.currency, price.price).amount)}}</b></span>
                            </div>
                          </div>
                        </span>
                        <span v-else-if="singleFacility.facility_type_id === 'Accomodation'">
                          <p class="price-value">Price {{$localCurrency}} {{$roundOff(convert1(singleFacility.price[0].currency_type, singleFacility.price[0].mandatory_price).amount)}}</p>
                        </span>
                        <span v-else-if="singleFacility.facility_type_id === 'Camping Sites'">
                          <p class="price-value">{{$localCurrency}} {{$roundOff(convert1(singleFacility.price[0].currency_type, singleFacility.price[0].mandatory_price).amount)}} exclusive of night charges</p>
                          <h5 class="price-range">Price {{$localCurrency}} {{$roundOff(convert1(singleFacility.price[0].currency_type, singleFacility.price[0].child_price).amount)}} - {{$localCurrency}} {{$roundOff(convert1(singleFacility.price[0].currency_type, singleFacility.price[0].adult_price).amount)}}</h5>
                        </span>
                      </div>

                      <div class="description" v-html="singleFacility.description"></div>
                    </div>

                    <div class="details-image">
                      <img :src="singleFacility.thumbnail" alt="Facility image" class="facility-image">
                    </div>
                  </div>
                </div>
              </div>

              <!-- Step 2 - Activities -->
              <div v-else-if="model === 2" key="step2" class="step-content">
                <div class="inventory-booking">
                  <inventory-booking></inventory-booking>
                </div>

                <div class="action-buttons">
                  <button class="earth-action-btn" @click="nextStep">
                    <span>Skip Activities </span>
                    <span>Done with Activities</span>
                    <svg class="arrow-icon" viewBox="0 0 24 24">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>

                <div class="divider"></div>

                <div class="inventory-details">
                  <div class="details-grid">
                    <div class="details-info">
                      <h3 class="inventory-name earthwerk-font">{{singleInventory.name}}</h3>

                      <div class="price-section">
                        <span v-if="singleInventory.pricing_status === 'true'">
                          <h6 class="price-value" v-if="browserCurrency === 'KES' && singleInventory.price.currency_type_id === 'USD'">Child Price: {{convertToKes(singleInventory.price.child_price)}}. Adult Price: {{convertToKes(singleInventory.price.adult_price)}}</h6>
                          <h6 class="price-value" v-if="browserCurrency === 'KES' && singleInventory.price.currency_type_id === 'KES'">Child Price: KES {{(singleInventory.price.child_price)}}. Adult Price: KES {{(singleInventory.price.adult_price)}}</h6>
                          <h6 class="price-value" v-if="browserCurrency === 'USD' && singleInventory.price.currency_type_id === 'USD'">Child Price: ${{(singleInventory.price.child_price)}}. Adult Price: ${{(singleInventory.price.adult_price)}}</h6>
                          <h6 class="price-value" v-if="browserCurrency === 'USD' && singleInventory.price.currency_type_id === 'KES'">Child Price: {{convertToUsd(singleInventory.price.child_price)}}. Adult Price: {{convertToUsd(singleInventory.price.adult_price)}}</h6>
                        </span>
                      </div>

                      <div class="description" v-html="singleInventory.description"></div>
                    </div>

                    <div class="details-image">
                      <img :src="singleInventory.thumbnail" alt="Activity image" class="inventory-image">
                    </div>
                  </div>
                </div>
              </div>

              <!-- Step 3 - Payment -->
              <div v-else-if="model === 3" key="step3" class="step-content">
                <booking-information></booking-information>
              </div>
            </transition>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import FrontNavigation from '../Repeated/FrontNavigation'
  import Search from '../Repeated/Search'
  import Activities from '../Repeated/Activities'
  import CampSites from '../Repeated/CampSites'
  import Accomodations from '../Repeated/Accomodation'
  import SideBar from '../Repeated/SideBar'
  import InventoryBooking from '../../Back/Bookings/Snippets/InventoryBooking'
  import FacilityBooking from '../../Back/Bookings/Snippets/FacilityBooking'
  import BookingInformation from './BookingInformation'

  // SVG Icons as components
  const BedIcon = {
    template: `<svg viewBox="0 0 24 24"><path d="M3 5v14h18V5H3zm16 12H5v-6h14v6zM5 7h14v2H5V7z"/></svg>`
  }

  const ActivityIcon = {
    template: `<svg viewBox="0 0 24 24"><path d="M13 2v2h6v2h-6v2h5v2h-5v2h4v2h-4v2h3v2h-3v2h2v2h-2v2h-2v-2H9v-2H7v-2h3v-2H7v-2h4v-2H7v-2h5v-2H7V8h6V6H7V4h6V2h2z"/></svg>`
  }

  const PaymentIcon = {
    template: `<svg viewBox="0 0 24 24"><path d="M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z"/></svg>`
  }

  const BedIconInactive = {
    template: `<svg viewBox="0 0 24 24"><path d="M3 5v14h18V5H3zm16 12H5v-6h14v6zM5 7h14v2H5V7z" fill-opacity="0.5"/></svg>`
  }

  const ActivityIconInactive = {
    template: `<svg viewBox="0 0 24 24"><path d="M13 2v2h6v2h-6v2h5v2h-5v2h4v2h-4v2h3v2h-3v2h2v2h-2v2h-2v-2H9v-2H7v-2h3v-2H7v-2h4v-2H7v-2h5v-2H7V8h6V6H7V4h6V2h2z" fill-opacity="0.5"/></svg>`
  }

  const PaymentIconInactive = {
    template: `<svg viewBox="0 0 24 24"><path d="M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z" fill-opacity="0.5"/></svg>`
  }

  export default {
    components: {
      BookingInformation,
      FacilityBooking,
      InventoryBooking,
      SideBar,
      Accomodations,
      CampSites,
      Activities,
      Search,
      FrontNavigation,
      BedIcon,
      ActivityIcon,
      PaymentIcon,
      BedIconInactive,
      ActivityIconInactive,
      PaymentIconInactive
    },
    data() {
      return {
        model: 1,
        steps: [
          {
            id: 1,
            title: 'Accommodation',
            activeIcon: 'BedIcon',
            inactiveIcon: 'BedIconInactive'
          },
          {
            id: 2,
            title: 'Activities',
            activeIcon: 'ActivityIcon',
            inactiveIcon: 'ActivityIconInactive'
          },
          {
            id: 3,
            title: 'Payment',
            activeIcon: 'PaymentIcon',
            inactiveIcon: 'PaymentIconInactive'
          }
        ]
      }
    },
    computed: {
      progressStyle() {
        const progress = ((this.model - 1) / (this.steps.length - 1)) * 100
        return {
          width: `${progress}%`
        }
      },
      modelConsole() {
        return null
      }
    },
    created() {
      if (JSON.parse(localStorage.getItem('user_data')).check_in.length === 0 ||
        JSON.parse(localStorage.getItem('user_data')).check_out.length === 0) {
        this.$router.push({ name: 'Index' })
      } else {
        const data = {
          check_in_date: JSON.parse(localStorage.getItem('user_data')).check_in,
          check_out_date: JSON.parse(localStorage.getItem('user_data')).check_out
        }
        this.$store.dispatch("check_date_facility_public_availability", data)
      }
    },
    methods: {
      nextStep() {
        if (this.model < this.steps.length) {
          this.model++
        }
      },
      openExpress: function () {
        this.$router.push({ name: 'ExpressCheckout' })
      }
    }
  }
</script>

<style scoped>
  @font-face {
    font-family: 'Earthwerk';
    src: url('PizzaDude.dk-Earthwerk.otf');
    font-weight: normal;
    font-style: normal;
  }

  .reservation-container {
    font-family: 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif;
    background-color: #f8f9fa;
    min-height: 100vh;
  }

  .main-navigation {
    background: white;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
    position: sticky;
    top: 0;
    z-index: 100;
  }

  .booking-wrapper {
    display: flex;
    max-width: 1440px;
    margin: 0 auto;
    padding: 20px;
    gap: 24px;
  }

  .sidebar-wrapper {
    flex: 0 0 300px;
  }

  .main-content {
    flex: 1;
    background: white;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    padding: 24px;
  }

  .search-wrapper {
    margin-bottom: 24px;
  }

  /* Stepper Section */
  .stepper-section {
    margin-top: 20px;
  }

  .stepper-header {
    position: relative;
    margin-bottom: 40px;
  }

  .stepper-progress {
    position: absolute;
    top: 50%;
    left: 0;
    height: 4px;
    background: #74a533;
    z-index: 1;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .stepper-steps {
    display: flex;
    justify-content: space-between;
    position: relative;
    z-index: 2;
  }

  .step {
    display: flex;
    flex-direction: column;
    align-items: center;
    cursor: pointer;
    position: relative;
    flex: 1;
  }

    .step:not(:last-child)::after {
      content: '';
      position: absolute;
      top: 16px;
      left: 60%;
      right: -40%;
      height: 4px;
      background: #e9ecef;
      z-index: -1;
    }

  .step-icon {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: white;
    border: 2px solid #e9ecef;
    margin-bottom: 8px;
    transition: all 0.3s ease;
  }

  .step.active .step-icon {
    border-color: #74a533;
    background: #74a533;
  }

  .step.completed .step-icon {
    border-color: #74a533;
    background: #74a533;
  }

  .step svg {
    width: 20px;
    height: 20px;
    fill: currentColor;
  }

  .step.active svg {
    fill: white;
  }

  .step.completed svg {
    fill: white;
  }

  .step.inactive svg {
    fill: #adb5bd;
  }

  .step-label {
    text-align: center;
  }

  .step-number {
    display: block;
    font-size: 12px;
    color: #adb5bd;
    font-weight: 500;
  }

  .step-title {
    display: block;
    font-size: 14px;
    font-weight: 600;
    color: #6c757d;
  }

  .step.active .step-number,
  .step.completed .step-number {
    color: #74a533;
  }

  .step.active .step-title,
  .step.completed .step-title {
    color: #333;
  }

  /* Step Content */
  .stepper-content {
    position: relative;
    min-height: 400px;
    overflow: hidden;
  }

  .step-content {
    padding: 16px 0;
  }

  /* Transitions */
  .slide-fade-enter-active {
    transition: all 0.3s ease-out;
  }

  .slide-fade-leave-active {
    transition: all 0.2s cubic-bezier(1, 0.5, 0.8, 1);
  }

  .slide-fade-enter-from,
  .slide-fade-leave-to {
    transform: translateX(20px);
    opacity: 0;
  }

  .icon-transition-enter-active,
  .icon-transition-leave-active {
    transition: all 0.3s ease;
  }

  .icon-transition-enter-from,
  .icon-transition-leave-to {
    opacity: 0;
    transform: scale(0.8);
  }

  /* Action Buttons */
  .action-buttons {
    margin: 24px 0;
    display: flex;
    justify-content: center;
  }

  .earth-action-btn {
    background: rgba(116, 165, 51, 0.9);
    color: white;
    border: none;
    padding: 12px 24px;
    font-size: 14px;
    font-weight: 600;
    border-radius: 4px;
    cursor: pointer;
    box-shadow: 0 4px 8px rgba(116, 165, 51, 0.3);
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 8px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

    .earth-action-btn:hover {
      background: rgba(116, 165, 51, 1);
      transform: translateY(-2px);
      box-shadow: 0 6px 12px rgba(116, 165, 51, 0.4);
    }

    .earth-action-btn:active {
      transform: translateY(0);
    }

  .arrow-icon {
    width: 18px;
    height: 18px;
    stroke: currentColor;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
    fill: none;
    transition: all 0.3s ease;
  }

  .earth-action-btn:hover .arrow-icon {
    transform: translateX(3px);
  }

  /* Divider */
  .divider {
    height: 1px;
    background: linear-gradient(to right, transparent, #dee2e6, transparent);
    margin: 32px 0;
  }

  /* Details Sections */
  .details-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 32px;
  }

  .details-info {
    padding: 16px;
  }

  .details-image {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .earthwerk-font {
    font-family: 'Earthwerk', sans-serif;
    color: #333;
    font-size: 24px;
    line-height: 1.3;
    margin-bottom: 16px;
    letter-spacing: 0.5px;
    text-transform: uppercase;
  }

  .facility-name, .inventory-name {
    color: #74a533;
    margin-bottom: 16px;
  }

  .price-section {
    margin-bottom: 20px;
  }

  .price-label {
    font-size: 16px;
    color: #666;
    margin-bottom: 8px;
  }

  .price-value {
    font-size: 16px;
    color: #333;
    font-weight: 500;
  }

  .price-range {
    color: #74a533;
    margin-top: 8px;
    font-size: 16px;
  }

  .price-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;
  }

  .price-chip {
    background: #f0f0f0;
    padding: 6px 12px;
    border-radius: 4px;
    font-size: 14px;
    color: #333;
  }

  .description {
    line-height: 1.6;
    color: #666;
  }

    .description > > > p {
      margin-bottom: 12px;
    }

  .facility-image, .inventory-image {
    width: 100%;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    object-fit: cover;
    max-height: 400px;
  }

  /* Responsive Adjustments */
  @media (max-width: 992px) {
    .booking-wrapper {
      flex-direction: column;
    }

    .sidebar-wrapper {
      margin-bottom: 24px;
    }

    .details-grid {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 768px) {
    .stepper-steps {
      flex-direction: column;
      align-items: flex-start;
      gap: 20px;
    }

    .step {
      flex-direction: row;
      width: 100%;
      align-items: center;
      gap: 12px;
    }

      .step:not(:last-child)::after {
        display: none;
      }

    .step-icon {
      margin-bottom: 0;
    }

    .step-label {
      text-align: left;
    }

    .stepper-progress {
      display: none;
    }

    .earthwerk-font {
      font-size: 20px;
    }
  }

  @media (max-width: 600px) {
    .earthwerk-font {
      font-size: 18px;
    }

    .main-content {
      padding: 16px;
    }

    .earth-action-btn {
      padding: 10px 16px;
      font-size: 13px;
    }
  }
</style>
